// frontend/src/hooks/useOpenPurchaseOrder.js
//
// Open a purchase order's details tab — the one way any button in the SaaS
// reaches a PO, so one PO is never open in two tabs — and close it once the
// PO is gone.
//
// The page has two spellings (tabs/routeRegistry.jsx `/purchase-orders/:id/:mode?`):
// `/purchase-orders/:id` and `/purchase-orders/:id/receive`, the latter
// landing with every open line filled to its ordered quantity, ready to
// submit (the Home and list "Receive" buttons). Tabs are keyed by path, so a
// plain `openTab` of the other spelling would open the same PO a second
// time; this hook focuses whichever tab of the PO is already open and opens
// the requested spelling only when neither is. A details tab already open
// wins over `receive` — the merchant is one click (Receive) from the
// same place, and their tab keeps its state.

import { useCallback, useEffect, useRef } from 'react';
import { useTabs } from '../tabs/useTabs';

export const purchaseOrderPath = (id) => `/purchase-orders/${id}`;

const isTabOf = (tab, id) => tab.path === purchaseOrderPath(id) || tab.path === `${purchaseOrderPath(id)}/receive`;

// Read the tab list at call time through a ref so the callbacks below keep
// one identity: the PO list hands them to its grid cells, and a callback that
// changed on every tab open/close would re-render every row. Same mirror
// useTabs keeps for open().
function useTabsRef() {
  const { tabs, openTab, close } = useTabs();
  const tabsRef = useRef(tabs);
  useEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);
  return { tabsRef, openTab, close };
}

/**
 * @returns {(id: number|string, opts?: { receive?: boolean }) => void}
 */
export function useOpenPurchaseOrder() {
  const { tabsRef, openTab } = useTabsRef();
  return useCallback((id, { receive = false } = {}) => {
    const already = tabsRef.current.find((tab) => isTabOf(tab, id));
    const detail = purchaseOrderPath(id);
    openTab(already ? already.path : (receive ? `${detail}/receive` : detail));
  }, [tabsRef, openTab]);
}

/**
 * Close the PO's tab, if one is open — for a screen that deleted the PO
 * somewhere other than its own tab.
 * @returns {(id: number|string) => void}
 */
export function useClosePurchaseOrderTab() {
  const { tabsRef, close } = useTabsRef();
  return useCallback((id) => {
    const tab = tabsRef.current.find((t) => isTabOf(t, id));
    if (tab) close(tab.id);
  }, [tabsRef, close]);
}
